import { ArchType, TreatmentInfoQuestionType, Versions } from 'app/core/enums';
import { ToothNote } from './tooth-note.model';
import { PrintSettings } from './print-settings.model';
import { TreatmentImplant } from './treatment-implant.model';
import { TreatmentGuide } from './treatment-guide.model';
import { TreatmentRemovableDenture } from './treatment-removable-denture.model';
import { TreatmentClearAligner } from './treatment-clear-aligner.model';
import { TreatmentExtraMetadataModel } from './treatment-extra-metadata.model';

export class TreatmentInfoSection {
  name: string = '';
  description: string = '';
  questions: TreatmentInfoQuestion[] = [];
}

export class TreatmentInfoQuestion {
  id: number = 0;
  question: string = '';
  type: number = 0; // TreatmentInfoQuestionType
  required = false;
  answers: TreatmentInfoAnswer[] = [];
  value: any;
  isActive = false;
  isHidden = false;
  defaultSelected?: number;
  numberSliderOptions: any;
  image?: string | null;
  version: Versions = Versions.All;
  order = 0;
  description?: string | null;

  public isValid(): boolean {
    if (
      this.type === TreatmentInfoQuestionType.SingleChoice ||
      this.type === TreatmentInfoQuestionType.OneColSingleChoice ||
      this.type === TreatmentInfoQuestionType.OneColSingleChoiceShowDesc ||
      this.type === TreatmentInfoQuestionType.Open ||
      this.type === TreatmentInfoQuestionType.SingleDropdownChoice ||
      this.type === TreatmentInfoQuestionType.Slider ||
      this.type === TreatmentInfoQuestionType.Number ||
      this.type === TreatmentInfoQuestionType.TeethViewer
    ) {
      return this.value !== null && this.value !== undefined && this.value !== '';
    } else if (
      this.type === TreatmentInfoQuestionType.MultiChoice ||
      this.type === TreatmentInfoQuestionType.OneColMultiChoice
    ) {
      return this.answers.some(a => a.isSelected);
    }

    return true;
  }

  public areChildrenValid(): boolean {
    var valid = true;
    if (
      this.answers.some(a => (this.value === a.id || a.isSelected) && a.questions.some(q => q.required && q.isActive))
    ) {
      this.answers
        .filter(a => (this.value === a.id || a.isSelected) && a.questions.length > 0)
        .forEach(a =>
          a.questions
            .filter(q => q.required && q.isActive)
            .forEach(q => {
              valid = valid && q.isValid() && q.areChildrenValid();
            })
        );
    }

    return valid;
  }

  public get activeChildQuestions() {
    if (
      this.type === TreatmentInfoQuestionType.SingleChoice ||
      this.type === TreatmentInfoQuestionType.SingleDropdownChoice ||
      this.type === TreatmentInfoQuestionType.OneColSingleChoice ||
      this.type === TreatmentInfoQuestionType.OneColSingleChoiceShowDesc ||
      this.type === TreatmentInfoQuestionType.Toggle
    ) {
      return this.answers
        .filter(a => a.id === this.value)
        .map(a => a.questions)
        .reduce((a, b) => a.concat(b), []);
    } else if (
      this.type === TreatmentInfoQuestionType.MultiChoice ||
      this.type === TreatmentInfoQuestionType.Wrapper ||
      this.type === TreatmentInfoQuestionType.OneColMultiChoice
    ) {
      return this.answers
        .filter(a => a.isSelected)
        .map(a => a.questions)
        .reduce((a, b) => a.concat(b), []);
    }

    return [];
  }
}

export class TreatmentInfoAnswer {
  id: number = 0;
  name: string = '';
  description: string = '';
  isSelected: boolean = false;
  isActive: boolean = false;
  questions: TreatmentInfoQuestion[] = [];
}

export class TreatmentInfoResponse {
  questionId: number;
  answers: number[];
  answerText: string;
  childAnswerResponses: TreatmentInfoResponse[];
  version: Versions;

  constructor(
    id: number,
    answers: number[],
    answerText: string,
    childAnswerResponses: TreatmentInfoResponse[],
    version: Versions
  ) {
    this.questionId = id;
    this.answers = answers;
    this.answerText = answerText;
    this.childAnswerResponses = childAnswerResponses;
    this.version = version;
  }
}

export class TreatmentInfo<TInfo = any> {
  statusId?: number;
  treatmentTypeId: number = 0;
  teethNotes: ToothNote[] = [];
  archType!: ArchType;
  treatmentGuide?: TreatmentGuide;
  treatmentRemovableDenture?: TreatmentRemovableDenture;
  treatmentClearAligner?: TreatmentClearAligner;
  info?: TInfo[];
  treatmentPlan?: string;
  annotation?: string | null;
  caseDescription?: string | null;
  comment: string = '';
  treatmentSubTypeId: number = 0;
  buccalCoverage?: string;
  tightnessId?: number;
  coverageTypeId?: number;
  veneerType?: number;
  fabricationMethod: number = 0;
  backupQuantity: number = 0;
  // shade?: number = 0;
  printSettings?: PrintSettings[];
  isRushDesign = false;
  treatmentImplant = new TreatmentImplant();

  // primarySet?: number | null | undefined = 0;
  // backupSet?: number | null | undefined = 0;
  // unfinishedSet?: number = 0;
  // nightGuardSet?: boolean = false;

  isLegacyToothSelection: boolean = false;

  selectedTeeth?: string | null;

  userComment: string = '';
  unsanitizedComment: string = '';

  externalTinyID?: string | null;
  isSkipDraftStatusCheck?: boolean = false;
  extraMetadata = new TreatmentExtraMetadataModel();
}

export class TreatmentInfoResponseViewModel {
  info: TreatmentInfoResponse[] = [];
  annotation: string = '';
}

export class TreatmentInfoResponseInputModel {
  id: string = '';
  info: TreatmentInfoResponse[] = [];
  annotation: string = '';
  archType: ArchType = ArchType.Both;
  teethNotes: ToothNote[] = [];
}

export class TreatmentShippingTrackingNumberInputModel {
  trackingNumber: string = '';
  trackingURL: string = '';
}
